import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Commande, CommandeRequest, StatutCommande } from '../../types';
import { commandeService } from '../../services/commandeService';

interface CommandesState {
  list: Commande[];
  selected: Commande | null;
  loading: boolean;
  error: string | null;
}

const initialState: CommandesState = {
  list: [],
  selected: null,
  loading: false,
  error: null,
};

export const fetchCommandes = createAsyncThunk('commandes/fetchAll', () => commandeService.getAll());
export const fetchCommandesByClient = createAsyncThunk('commandes/fetchByClient', (clientId: number) =>
  commandeService.getByClient(clientId)
);
export const fetchCommandeById = createAsyncThunk('commandes/fetchById', (id: number) =>
  commandeService.getById(id)
);
export const createCommande = createAsyncThunk<Commande, CommandeRequest, { rejectValue: string }>(
  'commandes/create',
  async (data, { rejectWithValue }) => {
    try {
      return await commandeService.create(data);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.erreur ?? 'Impossible de créer la commande.');
    }
  }
);
export const updateCommande = createAsyncThunk<Commande, { id: number; data: CommandeRequest }, { rejectValue: string }>(
  'commandes/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await commandeService.update(id, data);
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.erreur ?? 'Impossible de modifier la commande.');
    }
  }
);
export const updateStatutCommande = createAsyncThunk(
  'commandes/updateStatut',
  ({
    id,
    statut,
    dateEcheance,
    numeroFacture,
    numeroBonCommande,
  }: {
    id: number;
    statut: StatutCommande;
    dateEcheance?: string;
    numeroFacture?: string;
    numeroBonCommande?: string;
  }) => commandeService.updateStatut(id, statut, dateEcheance, numeroFacture, numeroBonCommande)
);
export const traiterRenouvellementCommande = createAsyncThunk(
  'commandes/traiterRenouvellement',
  (id: number) => commandeService.traiterRenouvellement(id)
);
export const deleteCommande = createAsyncThunk<number, number, { rejectValue: string }>(
  'commandes/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await commandeService.delete(id);
      return id;
    } catch (e: any) {
      const message = e?.response?.data?.erreur
        ?? e?.response?.data?.message
        ?? e?.message
        ?? 'Impossible de supprimer cette commande.';
      return rejectWithValue(message);
    }
  }
);

const commandesSlice = createSlice({
  name: 'commandes',
  initialState,
  reducers: {
    clearSelected: (state) => { state.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommandes.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCommandes.fulfilled, (state, action: PayloadAction<Commande[]>) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchCommandes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Erreur de chargement';
      })
      .addCase(fetchCommandesByClient.fulfilled, (state, action: PayloadAction<Commande[]>) => {
        state.list = action.payload;
        state.loading = false;
      })
      .addCase(fetchCommandeById.fulfilled, (state, action: PayloadAction<Commande>) => {
        state.selected = action.payload;
      })
      .addCase(createCommande.fulfilled, (state, action: PayloadAction<Commande>) => {
        state.list.unshift(action.payload);
      })
      .addCase(updateCommande.fulfilled, (state, action: PayloadAction<Commande>) => {
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.selected?.id === action.payload.id) state.selected = action.payload;
      })
      .addCase(updateStatutCommande.fulfilled, (state, action: PayloadAction<Commande>) => {
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.selected?.id === action.payload.id) state.selected = action.payload;
      })
      .addCase(traiterRenouvellementCommande.fulfilled, (state, action: PayloadAction<Commande>) => {
        const idx = state.list.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
        if (state.selected?.id === action.payload.id) state.selected = action.payload;
      })
      .addCase(deleteCommande.fulfilled, (state, action: PayloadAction<number>) => {
        state.list = state.list.filter((c) => c.id !== action.payload);
      });
  },
});

export const { clearSelected } = commandesSlice.actions;
export default commandesSlice.reducer;
